import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  HStack,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { Trans } from '@lingui/react/macro'
import { FaRedo } from 'react-icons/fa'

import type { RequestError } from '../../../components/api/extractHttpError'
import type { AskQuestionPayload } from './useAskCopilot'

type RetryConfig = {
  feedItemUid: string
  originalPayload: AskQuestionPayload
  isRetrying: boolean
  onRetry: (payload: AskQuestionPayload & { retryFeedItemUid: string }) => void
}

type AskCopilotErrorProps = {
  question: string
  error?: RequestError
  retryConfig?: RetryConfig
}

const opacity = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`

export const AskCopilotError = ({
  question,
  error,
  retryConfig,
}: AskCopilotErrorProps) => {
  const handleRetry = () => {
    if (!retryConfig) return

    retryConfig.onRetry({
      ...retryConfig.originalPayload,
      retryFeedItemUid: retryConfig.feedItemUid,
    })
  }

  return (
    <Box
      width="clamp(240px, 680px, 100%)"
      mx="auto"
      animation={`${opacity} 140ms ease-in`}
    >
      <VStack spacing="0" align="stretch">
        <VStack alignItems="center" spacing={0}>
          <Box height="17px" width="1px" bg="greys.300" mt={2} />
          <Box mt={2} width="100%" borderRadius="md" overflow="hidden">
            <Alert status="warning" py={2} pl={4} pr={3} variant="left-accent">
              <VStack gap={1} w="100%" alignItems="stretch">
                <HStack justifyContent="space-between" w="100%">
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color="greys.400"
                    textTransform="uppercase"
                    flex={1}
                  >
                    {question}
                  </Text>
                  {retryConfig ? (
                    <IconButton
                      aria-label="Retry sending message"
                      icon={<FaRedo />}
                      onClick={handleRetry}
                      isDisabled={retryConfig.isRetrying}
                      size="xs"
                      color="signal.cautionDark"
                      variant="ghost"
                      _hover={{
                        bg: 'none',
                        color: 'signal.caution',
                      }}
                    />
                  ) : null}
                </HStack>
                <HStack alignItems="flex-start" gap={1}>
                  <AlertIcon mr={0} />
                  <AlertTitle fontWeight="semibold">
                    {error?.name || <Trans>Failed to send message</Trans>}
                  </AlertTitle>
                  {error?.message && (
                    <AlertDescription wordBreak="break-word">
                      {error.message}
                    </AlertDescription>
                  )}
                </HStack>
              </VStack>
            </Alert>
          </Box>
        </VStack>
      </VStack>
    </Box>
  )
}
